import { useEffect, useState } from 'react';

import { Grid } from '@mantine/core';
import { useSelector } from 'react-redux';

import { RootState } from '@/store';
import { useAppSelector } from '@/store/hooks';
import { selectPatientDetails } from '@/store/selectors/transmissionReportPatientSelectors';
import { useGetOrgByIdQuery } from '@/store/slices/api/organizationApiSlice';
import {
  useGetAuthorizedPractitionersQuery,
  useGetInClinicPractitionersQuery
} from '@/store/slices/api/patientsApiSlice';
import { isInternalPortalRole } from '@/utils/globalFilterClinics';
import InfoMessage from '@/components/InfoMessage';

import XifinOrderingPhysician from '@/pages/physician/PatientOverviewPage/PatientDetails/components/TabsSection/CareTeamDetails/XifinOrderingPhysician';
import ImplantingEPSelect from '@/pages/physician/TransmissionReportPage/ReportPatientDetails/CareTeamDetails/components/ImplantingEPSelect';
import PractitionerSelect from '@/pages/physician/TransmissionReportPage/ReportPatientDetails/CareTeamDetails/components/PractitionerSelect';
import ReferringProviderSelect from '@/pages/physician/TransmissionReportPage/ReportPatientDetails/CareTeamDetails/components/ReferringProviderSelect';

interface CareTeamDetailsProps {
  patientId: string;
}

const CareTeamDetails = ({ patientId }: CareTeamDetailsProps) => {
  const patientDetails = useSelector((state: RootState) => selectPatientDetails(state, patientId));
  const isInternalUser = useAppSelector((state) => isInternalPortalRole(state.auth.user?.role));

  const implantingProviderType = useGetOrgByIdQuery(patientDetails?.clinic?.id ?? '', {
    skip: !patientDetails?.clinic?.id
  }).data?.organization?.parent?.implantingProviderType;

  const [followingPractitionerId, setFollowingPractitionerId] = useState<string | null>(null);
  const [inClinicPractitionerId, setInClinicPractitionerId] = useState<string | null>(null);

  const { data: patientPractitioners, isLoading: isPatientPractitionersLoading } =
    useGetAuthorizedPractitionersQuery(
      {
        patientId
      },
      {
        skip: !patientId
      }
    );

  const { data: inClinicPractitioners, isLoading: isInClinicPractitionersLoading } =
    useGetInClinicPractitionersQuery(
      {
        patientId
      },
      {
        skip: !patientId
      }
    );

  useEffect(() => {
    setFollowingPractitionerId(patientDetails?.practitioner?.id ?? null);
    setInClinicPractitionerId(patientDetails?.inClinicPractitioner?.userId ?? null);
  }, [patientDetails?.practitioner?.id, patientDetails?.inClinicPractitioner?.userId, patientId]);

  if (!patientDetails) {
    return (
      <InfoMessage
        title={'Failed To Load Care Team Details'}
        message={'Please refresh the page or contact support if the issue persists.'}
      />
    );
  }

  return (
    <Grid>
      <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
        <PractitionerSelect
          label="Following Practitioner"
          value={followingPractitionerId}
          patientId={patientId}
          isReportEditMode={true}
          patientPractitioners={patientPractitioners}
          isPatientPractitionersLoading={isPatientPractitionersLoading}
          updateField={{ patient: 'practitioner' }}
          onUpdate={setFollowingPractitionerId}
          labelVariant="sentence"
          editOnHover
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
        <ReferringProviderSelect
          clinicId={patientDetails.clinic?.id ?? ''}
          patientId={patientId}
          referringPhysician={patientDetails?.referringPhysician}
          referringProvider={patientDetails?.referringProvider}
          isReportEditMode={true}
          labelVariant="sentence"
          editOnHover
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
        <PractitionerSelect
          label="In Clinic Practitioner"
          value={inClinicPractitionerId}
          patientId={patientId}
          isReportEditMode={true}
          patientPractitioners={inClinicPractitioners}
          isPatientPractitionersLoading={isInClinicPractitionersLoading}
          updateField={{ patient: 'inClinicPractitioner' }}
          onUpdate={setInClinicPractitionerId}
          labelVariant="sentence"
          editOnHover
        />
      </Grid.Col>
      {isInternalUser && (
        <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
          <XifinOrderingPhysician patientId={patientId} />
        </Grid.Col>
      )}
      <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
        <ImplantingEPSelect
          patientId={patientId}
          implantingEp={patientDetails?.implantingEp}
          implantingProviderType={implantingProviderType as any}
          implantingEpFreeText={patientDetails?.implantingEpFreeText}
          labelVariant="sentence"
          editOnHover
        />
      </Grid.Col>
    </Grid>
  );
};

export default CareTeamDetails;
